import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { getAdminTraffic } from "../lib/api";
import usePageMeta from "../hooks/usePageMeta";

function Bar({ value, max, colour = "#c9a04b" }) {
  const w = max > 0 ? Math.max(3, (value / max) * 100) : 0;
  return (
    <div className="h-1.5 bg-cream/10 relative">
      <div style={{ width: `${w}%`, background: colour }} className="absolute inset-y-0 left-0" />
    </div>
  );
}

export default function AdminTraffic() {
  usePageMeta({ title: "Admin · Traffic", path: "/admin/traffic" });
  const [params, setParams] = useSearchParams();
  const key = params.get("key") || "";
  const [days, setDays] = useState(Number(params.get("days")) || 30);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [inputKey, setInputKey] = useState(key);

  useEffect(() => {
    if (!key) return;
    setLoading(true);
    setError(null);
    getAdminTraffic(key, days)
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => {
        setError(e?.response?.status === 404 ? "Not found." : "Load failed.");
        setLoading(false);
      });
  }, [key, days]);

  const maxDaily = useMemo(() => Math.max(1, ...(data?.daily?.map((d) => d.views) || [1])), [data]);
  const maxPage  = useMemo(() => Math.max(1, ...(data?.top_pages?.map((d) => d.views) || [1])), [data]);
  const maxRef   = useMemo(() => Math.max(1, ...(data?.top_referrers?.map((d) => d.views) || [1])), [data]);

  // Gate: no key → login form. Wrong key → 404-style.
  if (!key) {
    return (
      <div data-testid="admin-gate" className="min-h-screen bg-charcoal text-cream flex items-center justify-center px-6">
        <form
          onSubmit={(e) => { e.preventDefault(); setParams({ key: inputKey, days: String(days) }); }}
          className="max-w-sm w-full border border-gold/25 p-8"
        >
          <div className="eyebrow mb-4 text-gold/80">Admin Access</div>
          <h1 className="font-serif text-3xl text-cream mb-6">Traffic</h1>
          <input
            type="password"
            value={inputKey}
            onChange={(e) => setInputKey(e.target.value)}
            data-testid="admin-key-input"
            placeholder="Access key"
            className="w-full bg-charcoal border border-cream/20 text-cream px-3 py-2 mb-4 focus:border-gold outline-none"
            autoFocus
          />
          <button
            data-testid="admin-key-submit"
            className="w-full bg-gold text-charcoal text-xs uppercase tracking-[0.22em] py-3 hover:bg-cream transition-colors"
          >
            Unlock
          </button>
        </form>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-cream/60" data-testid="admin-error">
        {error}
      </div>
    );
  }

  return (
    <div data-testid="page-admin-traffic" className="max-w-6xl mx-auto px-6 md:px-10 py-16">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="eyebrow text-gold/80 mb-2">Traffic Dashboard</div>
          <h1 className="font-serif text-4xl md:text-5xl text-cream">
            Last <em className="text-gold">{data?.range_days ?? days}</em> days
          </h1>
        </div>
        <div className="flex gap-2 text-[11px] uppercase tracking-[0.22em]">
          {[7, 30, 90].map((n) => (
            <button
              key={n}
              onClick={() => { setDays(n); setParams({ key, days: String(n) }); }}
              data-testid={`admin-range-${n}`}
              className={`px-3 py-2 border transition-colors ${
                days === n ? "border-gold text-gold" : "border-cream/25 text-cream/60 hover:text-cream"
              }`}
            >
              {n}d
            </button>
          ))}
        </div>
      </div>

      {loading || !data ? (
        <div className="text-cream/50">Loading...</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gold/15 mb-12">
            <Stat label="Total views" value={data.total_views} />
            <Stat label="Unique visitors" value={data.unique_visitors} />
            <Stat label="Pages/visitor" value={data.unique_visitors ? (data.total_views / data.unique_visitors).toFixed(1) : "0"} />
            <Stat label="Top page" value={data.top_pages[0]?.path || "-"} small />
          </div>

          <div className="grid md:grid-cols-2 gap-10 mb-12">
            <Panel title="Top pages" data-testid="admin-top-pages">
              {data.top_pages.length === 0 ? (
                <Empty />
              ) : data.top_pages.map((p) => (
                <Row key={p.path} label={p.path} value={p.views} max={maxPage} />
              ))}
            </Panel>
            <Panel title="Top referrers" data-testid="admin-top-refs">
              {data.top_referrers.length === 0 ? (
                <Empty />
              ) : data.top_referrers.map((r) => (
                <Row key={r.source} label={r.source} value={r.views} max={maxRef} />
              ))}
            </Panel>
          </div>

          <Panel title="Daily views" data-testid="admin-daily">
            {data.daily.length === 0 ? (
              <Empty />
            ) : (
              <div className="flex items-end gap-1 h-40 mt-4">
                {data.daily.map((d) => (
                  <div key={d.date} className="flex-1 flex flex-col items-center gap-2 group">
                    <div className="flex-1 w-full flex items-end">
                      <div
                        style={{ height: `${(d.views / maxDaily) * 100}%` }}
                        className="w-full bg-gold/70 group-hover:bg-gold transition-colors min-h-[2px]"
                        title={`${d.date}: ${d.views}`}
                      />
                    </div>
                    <span className="text-[9px] text-cream/40 rotate-45 origin-left whitespace-nowrap">
                      {d.date.slice(5)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          <p className="mt-16 text-xs text-cream/40 tracking-wider">
            Bots and admin routes are excluded. Visitor IDs are hashed - no IP or personal data is stored in plain text.
          </p>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, small }) {
  return (
    <div className="bg-charcoal p-6">
      <div className="eyebrow mb-2 text-cream/50">{label}</div>
      <div className={small ? "font-serif text-xl text-cream truncate" : "font-serif text-4xl text-gold"}>
        {value}
      </div>
    </div>
  );
}

function Panel({ title, children, ...rest }) {
  return (
    <div className="border border-gold/15 p-6" {...rest}>
      <div className="eyebrow mb-4 text-gold/70">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Row({ label, value, max }) {
  return (
    <div>
      <div className="flex justify-between text-sm text-cream/80 mb-1">
        <span className="truncate mr-3">{label}</span>
        <span className="text-gold tabular-nums">{value}</span>
      </div>
      <Bar value={value} max={max} />
    </div>
  );
}

function Empty() {
  return <div className="text-cream/40 text-sm">No data yet.</div>;
}
